import { Injectable } from '@angular/core';
import { ApplicationUser } from '../viewmodels/application-user';
import { Router } from '@angular/router';
import { AuthenticationObserver } from './authentication.observer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticationResult } from '../viewmodels/authetication-result';
import { Constants } from '../../shared/common/helper/constants';
import { HttpService } from '../../shared/common/helper/httpService';
import * as moment from 'moment';
import { Global } from '../../app.global';
import { GlobalObservables } from '../../app.global.observer';
import { QuestionService } from '../../shared/common/services/question.service';
import { PcnBlockService } from '../../shared/common/services/pcn-block.service';
import { SettingsService } from './settings.service';
import { PasswordResetViewModel } from '../viewmodels/reset-password-viewmodel';
import { PasswordResetRequestViewModel } from '../viewmodels/reset-password-request-viewmodel';
import { UnlockAccountViewModel } from '../viewmodels/unlock-account-viewmodel';
import { ActivateAccountViewModel } from '../viewmodels/activate-account-viewmodel';
import { ReasonService } from '../../shared/common/services/reason.service';
import { StorageService } from '../../shared/common/services/storage.service';
import { sessionValidationResultModel } from '../viewmodels/session-validation-result.viewmodel';
import { UserEnvironmentService } from '../../shared/common/services/userenvironment.service';
import { MfaConfiguration } from '../viewmodels/mfa-configuration.viewmodel';

@Injectable()
export class AuthenticationService {
  token: string;

  private accountServiceUrl = Constants.serverUrl + 'api/account/';

  // store the URL so we can redirect after logging in
  private getTokenUrl = '/api/connect/token';

  constructor(
    public http: HttpService,
    private router: Router,
    private authenticationObserver: AuthenticationObserver,
    private global: Global,
    private globalObs: GlobalObservables,
    private questionsServ: QuestionService,
    private pcnService: PcnBlockService,
    private reasonService: ReasonService,
    private settingsService: SettingsService,
    private storageService: StorageService,
    private userEnvironmentService: UserEnvironmentService
  ) {}

  login(user: ApplicationUser): Observable<AuthenticationResult> {
    return new Observable((observer) => {
      this.userEnvironmentService
        .getEnvironmentDetails()
        .then((environmentDetails) => {
          user.deviceFingerprint = environmentDetails.deviceFingerprint;
          user.ipAddress = environmentDetails.ipAddress;
          user.country = environmentDetails.country;
          user.region = environmentDetails.region;
          user.city = environmentDetails.city;

          const data = JSON.stringify(user);
          this.http.UnsecuredPost(this.accountServiceUrl + 'login', data).subscribe({
            next: (result: AuthenticationResult) => {
              if (result) {
                const authResult = new AuthenticationResult({
                  isPasswordValid: result.isPasswordValid,
                  isMfaRequired: result.isMfaRequired,
                  isAccountLocked: result.isAccountLocked,
                  isAccountActivated: result.isAccountActivated,
                  authenticationFailedReason: result.authenticationFailedReason,
                  accessLevel: result.accessLevel,
                  mfaRequestReason: result.mfaRequestReason,
                  user: result.user,
                  userData: result.userData,
                  token: result.token,
                  tokenExpirationDate: result.tokenExpirationDate,
                });

                if (authResult.isAuthenticated) {
                  this.authenticate(
                    authResult.user,
                    authResult.userData,
                    authResult.token,
                    authResult.tokenExpirationDate
                  );
                }

                // Emit the full authentication result
                observer.next(authResult);
              } else {
                observer.next(
                  new AuthenticationResult({
                    isAuthenticated: false,
                    authenticationFailedReason: 'Authentication failed',
                  })
                );
              }
              observer.complete();
            },
            error: (error) => {
              console.log(error);
              observer.error(error);
            },
          });
        })
        .catch((error) => {
          console.log(error);
          observer.error(error);
        });
    });
  }

  authenticate(
    user: ApplicationUser,
    userData: any,
    accessToken: string,
    expiresIn: Date
  ): boolean {
    try {
      if (user) {
        // document.cookie = 'rm4=' + accessToken;
        // localStorage.setItem('rm_current_user', JSON.stringify(user));
        this.storageService.setCurrentUser(user);
        // localStorage.setItem('rm_user_data', JSON.stringify(userData));
        // localStorage.setItem('rm_access_token', accessToken);
        this.storageService.setAccessToken(accessToken);
        // localStorage.setItem('rm_expires_in', JSON.stringify(expiresIn));
        this.storageService.setTokenExpirationDate(expiresIn);
      }

      // Reset application-specific details
      this.resetDetails();
      this.pcnService.setClosePcn(false);
      this.storageService.lockReasons(false); // Ensure reasons panel is not locked
      this.global.isPcnClosed = false;
      this.global.isReasonsLocked = false;
      this.storageService.lockPcnInput(false); // Ensure PCN input is not locked
      // this.settingsService.setLocalDebugConsole(false);

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Log out
   * clear translations
   * inform other components about authentication status
   * @returns {}
   */

  logout(): void {
    // clear token remove user from local storage to log user out
    this.token = null;

    this.storageService.clearSessionStorage();
    this.storageService.clearLocalStorage();

    // localStorage.removeItem('rm_current_user');
    // localStorage.removeItem('rm_user_data');
    // localStorage.removeItem('rm_expires_in');
    // localStorage.removeItem('rm_id_token');
    // localStorage.removeItem('rm_pcn');
    // localStorage.removeItem('rm_reasons');
    localStorage.removeItem('rm_questions_answers');

    this.pcnService.setClosePcn(false);
    this.storageService.lockReasons(false); // unlock the reasons "panel"
    // this.global.lockReasons(false);
    this.global.isPcnClosed = false;
    this.global.isReasonsLocked = false;
    this.router.navigate(['/login']);
  }

  requestPasswordReset(resetRequest: PasswordResetRequestViewModel): Observable<any> {
    const data = JSON.stringify(resetRequest);
    return this.http.UnsecuredPost(this.accountServiceUrl + 'requestPasswordReset', data);
  }

  resetPassword(resetPassword: PasswordResetViewModel): Observable<any> {
    const data = JSON.stringify(resetPassword);
    return this.http.UnsecuredPost(this.accountServiceUrl + 'resetPassword', data);
  }

  lockAccount(username: string): Observable<any> {
    return this.http.UnsecuredPost(this.accountServiceUrl + 'lockAccount', { username });
  }

  unlockAccount(unlockAccount: UnlockAccountViewModel): Observable<any> {
    const data = JSON.stringify(unlockAccount);
    return this.http.UnsecuredPost(this.accountServiceUrl + 'unlockAccount', data);
  }

  activateAccount(activateAccount: ActivateAccountViewModel): Observable<any> {
    const data = JSON.stringify(activateAccount);
    return this.http.UnsecuredPost(this.accountServiceUrl + 'activateAccount', data);
  }

  checkActivationTokenValid(activateAccountVM: ActivateAccountViewModel): Observable<any> {
    const data = JSON.stringify(activateAccountVM);
    return this.http.UnsecuredPost(this.accountServiceUrl + 'checkActivationTokenValid', data);
  }

  getPasswordComplexity(activateAccountVM: ActivateAccountViewModel): Observable<any> {
    const data = JSON.stringify(activateAccountVM);
    return this.http.UnsecuredPost(this.accountServiceUrl + 'getPasswordComplexity', data);
  }

  logoutAccount() {
    const url = this.accountServiceUrl + 'logout';
    return this.http.Post(url, null).pipe(
      map((response) => {
        // remove jwt token cookie
        document.cookie = 'rm4= ; expires = Thu, 01 Jan 1970 00:00:00 GMT';
        return response;
      })
    );
  }

  // get token
  getToken(): string {
    return this.storageService.getAccessToken();
    // if (localStorage.getItem('rm_access_token')) {
    //   const rmToken = localStorage.getItem('rm_access_token');
    //   return rmToken;
    // }
    // return null;
  }

  // get logged in user
  getTokenExpirationDate(): Date {
    if (this.storageService.localStorageItemExists('rm_token_expiration_date')) {
      const dateStr = this.storageService.getTokenExpirationDate();
      const momentDate = moment(dateStr, 'YYYY-MM-DD HH:mm:ss');
      if (momentDate.isValid()) {
        return momentDate.toDate();
      }
    }
    return null;
  }
  //   if (localStorage.getItem('rm_expires_in')) {
  //     const dateStr = localStorage.getItem('rm_expires_in');
  //     const momentDate = moment(dateStr, 'YYYY-MM-DD HH:mm:ss');
  //     if (momentDate.isValid()) {
  //       return momentDate.toDate();
  //     }
  //   }
  //   return null;
  // }

  // get logged in user
  getCurrentUser(): ApplicationUser {
    if (this.storageService.localStorageItemExists('rm_current_user')) {
      const user = this.storageService.getCurrentUser();
      return user;
    }
    return null;
  }
  // }
  //   if (localStorage.getItem('rm_current_user')) {
  //     const user = new ApplicationUser();
  //     const userData = JSON.parse(localStorage.getItem('rm_current_user'));
  //     user.username = userData.username;
  //     user.userId = userData.userId;
  //     user.adminAccess = userData.adminAccess;
  //     user.reportsAccess = userData.reportsAccess;
  //     user.editLetterAccess = userData.editLetterAccess;
  //     user.firstName = userData.firstName;
  //     user.lastName = userData.lastName;
  //     user.email = userData.email;
  //     user.acceptedTerms = userData.acceptedTerms;
  //     return user;
  //   }
  //   return null;
  // }

  // get logged in user data
  // getCurrentUserData() {
  //   if (localStorage.getItem('rm_user_data')) {
  //     const userData = JSON.parse(localStorage.getItem('rm_user_data'));
  //     return userData;
  //   }
  //   return null;
  // }

  // check if user is logged in
  isUserLoggedIn(): boolean {
    const user = this.getCurrentUser();
    if (user && user.username) {
      return true;
    }
    return false;
  }

  // check if user has admin access
  hasAdminAccess(): boolean {
    const user = this.getCurrentUser();
    return user.adminAccess;
  }

  // check if user has reports access
  hasReportsAccess(): boolean {
    const user = this.getCurrentUser();
    return user.reportsAccess;
  }

  // check if user has edit letter access
  hasEditLetterAccess(): boolean {
    const user = this.getCurrentUser();
    return user.editLetterAccess;
  }

  // check if user is the admin
  isUserAdmin(): boolean {
    const user = this.getCurrentUser();
    return user.adminAccess && user.username === 'admin';
  }

  /** resetDetails */
  resetDetails(): void {
    this.storageService.clearSessionStorageItem('rm_pcn');
    // localStorage.setItem('rm_pcn', null);
    this.storageService.clearSessionStorageItem('rm_reasons');
    // localStorage.setItem('rm_reasons', null);
    this.global.questionsAndAnswers = [];
    this.global.touchedQuestionIds.clear();
    this.questionsServ.setLocalQuestionsAndAnswers([]);
    this.global.PCN = null;
    // this.global.REASONS = [];
    this.global.resetSelectedReasons();
    // this.globalObs.onPcnChanged.next(null);
    // fire the event to notify that selected reasons have changed
    // this.globalObs.OnSelectedReasonsUpdated.next([]);
    // this.globalObs.changeREASONS.next([]);
  }

  isUserLoggedinAndTokenValid(): boolean {
    const currentUser = this.getCurrentUser();
    if (currentUser !== null && currentUser.username !== undefined) {
      const expirationDate = this.getTokenExpirationDate();

      if (expirationDate === null || expirationDate.getTime() <= new Date().getTime()) {
        return false;
      }
      return true;
    }
    return false;
  }

  validateSession(): Observable<sessionValidationResultModel> {
    const token = this.getToken();
    return this.http.get<sessionValidationResultModel>(
      this.accountServiceUrl + 'validateSession/' + token
    );
  }

  getMfaConfiguration(username: string): Observable<any> {
    return this.http.UnsecuredPost(this.accountServiceUrl + 'getMfaConfiguration', { username });
  }

  requestMfaCode(
    username: string,
    ipAddress: string,
    deviceFingerprint: string,
    reasonForRequest: string
  ): Observable<any> {
    return this.http.UnsecuredPost(this.accountServiceUrl + 'requestMfaCode', {
      username,
      ipAddress,
      deviceFingerprint,
      reasonForRequest,
    });
  }

  verifyMfaCode(
    username: string,
    ipAddress: string,
    deviceFingerprint: string,
    deviceName: string,
    reasonForRequest: string,
    country: string,
    region: string,
    city: string,
    code: string,
    trust: boolean
  ): Observable<any> {
    return this.http.UnsecuredPost(this.accountServiceUrl + 'verifyMfaCode', {
      username,
      ipAddress,
      country,
      region,
      city,
      deviceFingerprint,
      deviceName,
      reasonForRequest,
      code,
      trust,
    });
  }
}
